import { Module } from 'vuex'

import {
  AuthStateModuleName,
  EditorPageStateModuleName,
  FreeSpaceJudgeDetailModuleName,
  JudgeDetailModuleName,
  JudgeStateModuleName,
  ModalStateModuleName,
  NotificationModuleName
} from '../modules'

import { JudgeRecord } from '@/domain/entities/judge/judgeRecord'

interface JudgeReferenceState {
  isLoading: boolean
  error: string | null
}

const DELAY_TIME = 1000
const FREE_SPACE_PROBLEM_ID = '8080'

export const judgeReferenceModule = (): Module<JudgeReferenceState, any> => {
  return {
    namespaced: true,
    state: {
      isLoading: false,
      error: null
    },
    mutations: {
      setLoading(state, isLoading: boolean) {
        state.isLoading = isLoading
      },
      setError(state, error: string | null) {
        state.error = error
      }
    },
    actions: {
      async handleJudgeRecord({ state, dispatch, commit, rootGetters }, { judgeRecord }: { judgeRecord: JudgeRecord }) {
        commit('setLoading', true)
        commit('setError', null)
        try {
          // ユーザーIDが異なる場合は処理を終了
          const userId = rootGetters[`${AuthStateModuleName}/user`].id
          if (userId !== judgeRecord.userId) {
            return
          }

          await commit(`${JudgeStateModuleName}/setJudgeId`, { id: judgeRecord.judgeId }, { root: true })

          // ジャッジが実行中または待機中の場合は処理を終了
          if (judgeRecord.status === 'RUNNING' || judgeRecord.status === 'WAITING') {
            return
          }

          //二重ポップ対策
          //直前の提出レコードのIDとstatusと同じならば表示させない
          const currentRecord = rootGetters['${JudgeStateModuleName}/judgeRecord']

          await commit(
            `${JudgeStateModuleName}/setJudgeRecord`,
            {
              record: judgeRecord
            },
            { root: true }
          )

          await dispatch(`${NotificationModuleName}/closeRunningNotification`, {}, { root: true })

          if (
            currentRecord && //直前のレコード自体が存在する
            currentRecord.judgeId === judgeRecord.judgeId && //IDが一致する
            currentRecord.status === judgeRecord.status //ステータスが一致する
          ) {
            return //強引に終了
          }

          if (judgeRecord.judgeType === 0) {
            // テストの場合
            const isShowFreeSpaceModal = rootGetters[`${ModalStateModuleName}/isShowFreeSpaceModal`]
            const isFreeSpaceMode = rootGetters[`${EditorPageStateModuleName}/isFreeSpaceMode`]

            // フリースペースの場合の通知
            if (isFreeSpaceMode || isShowFreeSpaceModal) {
              await dispatch(
                `${NotificationModuleName}/freeSpaceResultNotification`,
                {
                  showFreeSpaceModal: isShowFreeSpaceModal,
                  problemId: judgeRecord.problemId
                },
                { root: true }
              )
            } else {
              if (judgeRecord.status == 'ACCEPTED') {
                await dispatch(
                  `${NotificationModuleName}/acNotification`,
                  {
                    type: 'Judge',
                    problemId: judgeRecord.problemId,
                    judgeId: judgeRecord.judgeId
                  },
                  { root: true }
                )
              } else {
                await dispatch(
                  `${NotificationModuleName}/wrongNotification`,
                  {
                    status: judgeRecord.status,
                    type: 'Judge',
                    problemId: judgeRecord.problemId,
                    judgeId: judgeRecord.judgeId
                  },
                  { root: true }
                )
              }
            }
            // testJudgeDetailを取得
            if (judgeRecord.problemId == FREE_SPACE_PROBLEM_ID) {
              // フリースペースの場合
              await commit(`${JudgeStateModuleName}/setIsTestNotificationLoading`, false, { root: true })
              setTimeout(async () => {
                await dispatch(
                  `${FreeSpaceJudgeDetailModuleName}/getJudgeDetail`,
                  {
                    judgeId: judgeRecord.judgeId
                  },
                  { root: true }
                )
                await commit(`${JudgeStateModuleName}/setIsTestLoading`, false, { root: true })
              }, DELAY_TIME)
            } else {
              // テストジャッジの場合
              await commit(`${JudgeStateModuleName}/setIsTestNotificationLoading`, false, { root: true })

              setTimeout(async () => {
                await dispatch(
                  `${JudgeDetailModuleName}/getJudgeDetail`,
                  {
                    judgeId: judgeRecord.judgeId,
                    isTest: true
                  },
                  { root: true }
                )
                await commit(`${JudgeStateModuleName}/setIsTestLoading`, false, { root: true })
              }, DELAY_TIME)
            }
          } else {
            // 本提出の場合
            await commit(`${JudgeStateModuleName}/setIsJudgeNotificationLoading`, false, { root: true })
            if (judgeRecord.status == 'ACCEPTED') {
              await dispatch(
                `${NotificationModuleName}/acNotification`,
                {
                  type: 'Judge',
                  problemId: judgeRecord.problemId,
                  judgeId: judgeRecord.judgeId
                },
                { root: true }
              )
            } else {
              await dispatch(
                `${NotificationModuleName}/wrongNotification`,
                {
                  status: judgeRecord.status,
                  type: 'Judge',
                  problemId: judgeRecord.problemId,
                  judgeId: judgeRecord.judgeId
                },
                { root: true }
              )
            }
            setTimeout(async () => {
              await dispatch(
                `${JudgeDetailModuleName}/getJudgeDetail`,
                { judgeId: judgeRecord.judgeId },
                { root: true }
              )
              await commit(`${JudgeStateModuleName}/setIsJudgeLoading`, false, { root: true })
            }, DELAY_TIME)
            setTimeout(async () => {
              await dispatch(`${NotificationModuleName}/recommendEntryNotification`, {}, { root: true })
            }, DELAY_TIME)
          }
        } catch (e) {
          commit('setError', e)
        } finally {
          commit('setLoading', false)
        }
      }
    },
    getters: {
      isLoading: state => state.isLoading,
      error: state => state.error
    }
  }
}
